import { Box, Alert, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { LoadingSpinner } from '@components/LoadingSpinner';
import { AdminOfflineGuard } from '@components/guards/AdminOfflineGuard';
import useOnlineStatus from '@hooks/useOnlineStatus';
import { useGeneralSettingsData } from './hooks';
import { GeneralSettingsHeader, GeneralSettingsContent } from './components';

export const GeneralSettings = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isOnline = useOnlineStatus();

  const {
    showPricing,
    templateType,
    templateName,
    templateOptions,
    isLoadingTemplates,
    isLoading,
    error,
    hasUnsavedChanges,
    isSaving,
    saveError,
    updateShowPricing,
    updateTemplateType,
    updateTemplateName,
    saveChanges,
    discardChanges
  } = useGeneralSettingsData();

  const handleSave = async () => {
    await saveChanges();
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!isOnline) {
    return <AdminOfflineGuard children={null} />;
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {t('settings:generalSettings.errors.loadFailed')}
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        backgroundColor: theme.palette.background.default
      }}
    >
      <GeneralSettingsHeader
        hasUnsavedChanges={hasUnsavedChanges}
        isSaving={isSaving}
        onSave={handleSave}
        onDiscard={discardChanges}
      />

      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          minHeight: 0
        }}
      >
        {saveError && (
          <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, pt: 2 }}>
            <Alert severity="error">
              {t('settings:generalSettings.errors.saveFailed')}:{' '}
              {saveError.message}
            </Alert>
          </Box>
        )}

        <GeneralSettingsContent
          showPricing={showPricing}
          templateType={templateType}
          templateName={templateName}
          templateOptions={templateOptions}
          isLoadingTemplates={isLoadingTemplates}
          onToggleShowPricing={updateShowPricing}
          onChangeTemplateType={updateTemplateType}
          onChangeTemplateName={updateTemplateName}
        />
      </Box>
    </Box>
  );
};
